import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FolderOpen, Users, FileText, Receipt, TrendingUp, AlertCircle, Calendar, PiggyBank, BarChart3, ClipboardList, CheckCircle2, Clock, AlertTriangle, Briefcase, Activity, UserCheck, Circle } from 'lucide-react';
import { dashboardAPI, gastosAPI, tareasDespachoAPI } from '../api';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Función para obtener los permisos del usuario actual desde localStorage
const getUserPermisos = () => {
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      return user.permisos || {};
    }
  } catch (e) {
    console.error('Error parsing user data', e);
  }
  return {};
};

// Verificar si un widget es visible
const isWidgetVisible = (permisos, widgetId) => {
  const permiso = permisos[widgetId];
  // Si no hay permiso definido, mostrar por defecto
  if (permiso === undefined) return true;
  // Si es objeto, verificar lectura
  if (typeof permiso === 'object') return permiso.lectura !== false;
  // Si es booleano directo
  return permiso !== false;
};

const Panel = () => {
  const [stats, setStats] = useState({
    expedientes_activos: 0,
    expedientes_total: 0,
    actuaciones_total: 0,
    clientes_total: 0,
    facturacion_pendiente: 0,
    facturacion_cobrada: 0,
    eventos_proximos: 0,
    usuarios_conectados: 0,
    usuarios_conectados_lista: []
  });
  const [expedientesRecientes, setExpedientesRecientes] = useState([]);
  const [eventosProximos, setEventosProximos] = useState([]);
  const [gastosStats, setGastosStats] = useState({
    total_controles: 0,
    total_coste: 0,
    gastos_por_tipo: []
  });
  const [tareasStats, setTareasStats] = useState(null);
  const [tareasPendientes, setTareasPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permisos, setPermisos] = useState({});

  useEffect(() => {
    // Cargar permisos del usuario
    setPermisos(getUserPermisos());
    loadData();
    
    // Enviar heartbeat inicial
    sendHeartbeat();
    
    // Heartbeat cada 60 segundos para mantener sesión activa
    const heartbeatInterval = setInterval(sendHeartbeat, 60000);
    
    // Actualizar stats cada 30 segundos para usuarios conectados
    const statsInterval = setInterval(loadStats, 30000);
    
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(statsInterval);
    };
  }, []);

  const sendHeartbeat = async () => {
    try {
      const token = localStorage.getItem('lexduo_token');
      const userData = localStorage.getItem('user');
      if (!userData || !token) return;
      
      const user = JSON.parse(userData);
      await fetch(`${API_URL}/api/dashboard/heartbeat?user_id=${user.id}&nombre=${encodeURIComponent(user.nombre || '')}&email=${encodeURIComponent(user.email || '')}&rol=${encodeURIComponent(user.rol || '')}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error enviando heartbeat:', error);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('lexduo_token');
      const response = await fetch(`${API_URL}/api/dashboard/stats-extended`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(prev => ({
          ...prev,
          expedientes_activos: data.expedientes_activos || 0,
          expedientes_total: data.expedientes_total || 0,
          actuaciones_total: data.actuaciones_total || 0,
          clientes_total: data.clientes_total || 0,
          facturacion_pendiente: data.facturacion_pendiente || 0,
          facturacion_cobrada: data.facturacion_cobrada || 0,
          eventos_proximos: data.eventos_proximos || 0,
          usuarios_conectados: data.usuarios_conectados || 0,
          usuarios_conectados_lista: data.usuarios_conectados_lista || []
        }));
      }
    } catch (error) {
      console.error('Error cargando stats:', error);
    }
  };

  const loadData = async () => {
    try {
      // Cargar stats extendidas primero
      await loadStats();
      
      const [expRes, eventosRes, gastosRes, tareasStatsRes] = await Promise.all([
        dashboardAPI.getExpedientesRecientes(),
        dashboardAPI.getEventosProximos(),
        gastosAPI.getStats({}),
        tareasDespachoAPI.getStats().catch(() => ({ data: null }))
      ]);
      setExpedientesRecientes(expRes.data);
      setEventosProximos(eventosRes.data);
      setGastosStats(gastosRes.data);
      
      if (tareasStatsRes.data) {
        setTareasStats(tareasStatsRes.data);
        
        // Obtener tareas pendientes de cada categoría
        try {
          const [abogadosRes, costasRes, ejecRes] = await Promise.all([
            tareasDespachoAPI.abogados.getAll({ completada: false }),
            tareasDespachoAPI.costas.getAll({ completada: false }),
            tareasDespachoAPI.ejecuciones.getAll({ completada: false })
          ]);
          
          // Combinar y ordenar por prioridad/urgencia
          const todas = [
            ...(abogadosRes.data || []).map(t => ({ ...t, categoria: 'Abogados' })),
            ...(costasRes.data || []).map(t => ({ ...t, categoria: 'Costas' })),
            ...(ejecRes.data || []).map(t => ({ ...t, categoria: 'Ejecuciones' }))
          ];
          
          // Ordenar: urgentes primero, luego por fecha
          todas.sort((a, b) => {
            if (a.urgente && !b.urgente) return -1;
            if (!a.urgente && b.urgente) return 1;
            const prioA = a.prioridad === 'alta' ? 3 : a.prioridad === 'media' ? 2 : 1;
            const prioB = b.prioridad === 'alta' ? 3 : b.prioridad === 'media' ? 2 : 1;
            if (prioA !== prioB) return prioB - prioA;
            return new Date(a.fecha_limite || '9999-12-31') - new Date(b.fecha_limite || '9999-12-31');
          });
          
          setTareasPendientes(todas.slice(0, 8));
        } catch (e) {
          console.error('Error cargando tareas pendientes:', e);
        }
      }
    } catch (error) {
      console.error('Error cargando datos del panel:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Expedientes Activos',
      value: stats.expedientes_activos,
      icon: FolderOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/expedientes',
      widgetId: 'widget-expedientes-activos'
    },
    {
      title: 'Total Expedientes',
      value: stats.expedientes_total,
      icon: Briefcase,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      link: '/expedientes',
      widgetId: 'widget-expedientes-total'
    },
    {
      title: 'Total Actuaciones',
      value: stats.actuaciones_total,
      icon: Activity,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      link: '/actuaciones',
      widgetId: 'widget-actuaciones-total'
    },
    {
      title: 'Contactos',
      value: stats.clientes_total,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      link: '/contactos',
      widgetId: 'widget-contactos'
    },
    {
      title: 'Tareas Pendientes',
      value: tareasStats?.pendientes || 0,
      icon: ClipboardList,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      link: '/tareas',
      widgetId: 'widget-tareas-pendientes'
    },
    {
      title: 'Eventos Próximos',
      value: stats.eventos_proximos,
      icon: Calendar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      link: '/calendario',
      widgetId: 'widget-eventos'
    }
  ];

  // Filtrar tarjetas según permisos
  const visibleStatCards = statCards.filter(card => 
    isWidgetVisible(permisos, card.widgetId)
  );

  // Verificar visibilidad de secciones
  const showEstadisticas = isWidgetVisible(permisos, 'widget-estadisticas');
  const showEventos = isWidgetVisible(permisos, 'widget-eventos');
  const showExpedientesRecientes = isWidgetVisible(permisos, 'widget-expedientes-recientes');
  const showTareasPendientes = isWidgetVisible(permisos, 'widget-tareas-pendientes');
  const showFacturacion = isWidgetVisible(permisos, 'widget-facturacion');
  const showGastos = isWidgetVisible(permisos, 'widget-gastos');
  const showUsuariosConectados = isWidgetVisible(permisos, 'widget-usuarios-conectados');

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Panel de Control</h1>
        <p className="text-gray-600 mt-1">Vista general de tu despacho</p>
      </div>

      {/* Stats Grid - Solo si hay tarjetas visibles y estadísticas están habilitadas */}
      {showEstadisticas && visibleStatCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {visibleStatCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link key={stat.title} to={stat.link} data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                      </div>
                      <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Usuarios Conectados */}
      {showUsuariosConectados && (
        <Card data-testid="usuarios-conectados-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              Usuarios Conectados
              <Badge className="ml-2 bg-emerald-100 text-emerald-700">
                {stats.usuarios_conectados}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.usuarios_conectados_lista && stats.usuarios_conectados_lista.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.usuarios_conectados_lista.map((usuario) => (
                  <div 
                    key={usuario.id} 
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    data-testid={`usuario-conectado-${usuario.id}`}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-700 font-semibold text-sm">
                          {usuario.nombre?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <Circle className="w-3 h-3 text-emerald-500 fill-emerald-500 absolute -bottom-0.5 -right-0.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">{usuario.nombre || 'Usuario'}</p>
                      <p className="text-xs text-gray-500 truncate">{usuario.email}</p>
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {usuario.rol || 'Usuario'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                <UserCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No hay otros usuarios conectados</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Próximos Eventos */}
        {showEventos && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
              Próximos Eventos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {eventosProximos.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No hay eventos próximos</p>
              ) : (
                eventosProximos.map((evento) => (
                  <div key={evento.id} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="text-center min-w-[50px]">
                      <div className="text-xl font-bold text-blue-600">
                        {new Date(evento.fecha).getDate()}
                      </div>
                      <div className="text-xs text-gray-500 uppercase">
                        {new Date(evento.fecha).toLocaleDateString('es-ES', { month: 'short' })}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm">{evento.titulo}</h4>
                      <p className="text-xs text-gray-600 mt-1 truncate">{evento.tipo}</p>
                      {evento.hora_inicio && (
                        <p className="text-xs text-gray-500 mt-1">{evento.hora_inicio}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
              <Link to="/calendario" className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2">
                Ver todos los eventos →
              </Link>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Expedientes Recientes */}
        {showExpedientesRecientes && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen className="w-5 h-5 text-blue-600" />
              Expedientes Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {expedientesRecientes.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No hay expedientes recientes</p>
              ) : (
                expedientesRecientes.map((exp) => (
                  <Link key={exp.id} to={`/expedientes/${exp.id}`} className="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm">{exp.numero_expediente}</h4>
                        <p className="text-xs text-gray-600 mt-1 truncate">{exp.titulo}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{exp.rama}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            exp.estado === 'activo' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
                          }`}>{exp.estado}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
              <Link to="/expedientes" className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2">
                Ver todos los expedientes →
              </Link>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Tareas Pendientes */}
      {showTareasPendientes && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-lg">
              <ClipboardList className="w-5 h-5 text-orange-600" />
              Tareas Pendientes
            </span>
            <Link to="/tareas" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todas →
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Resumen de tareas */}
          {tareasStats && tareasStats.por_categoria && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {tareasStats.por_categoria.slice(0, 4).map((cat) => (
                <div key={cat.categoria} className="p-3 bg-gray-50 rounded-lg border">
                  <p className="text-xs text-gray-600 capitalize">{cat.nombre || cat.categoria}</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-xl font-bold text-gray-800">{cat.pendientes || 0}</p>
                    {cat.vencidas > 0 && (
                      <span className="text-xs text-red-500">({cat.vencidas} vencidas)</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Lista de tareas pendientes */}
          <div className="space-y-2">
            {tareasPendientes.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-2" />
                <p className="text-gray-500">¡No hay tareas pendientes!</p>
              </div>
            ) : (
              tareasPendientes.map((tarea) => {
                const isVencida = tarea.fecha_limite && new Date(tarea.fecha_limite) < new Date();
                const isProxima = tarea.fecha_limite && !isVencida && 
                  (new Date(tarea.fecha_limite) - new Date()) / (1000 * 60 * 60 * 24) <= 3;
                
                return (
                  <div 
                    key={`${tarea.categoria}-${tarea.id}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      tarea.urgente ? 'bg-red-50 border-red-200' : 
                      isVencida ? 'bg-orange-50 border-orange-200' :
                      isProxima ? 'bg-yellow-50 border-yellow-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      tarea.urgente ? 'bg-red-500' : 
                      isVencida ? 'bg-orange-500' :
                      isProxima ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {tarea.titulo || tarea.descripcion || `Tarea ${tarea.id}`}
                        </h4>
                        {tarea.urgente && (
                          <Badge className="bg-red-100 text-red-700 text-xs">Urgente</Badge>
                        )}
                        {isVencida && !tarea.urgente && (
                          <Badge className="bg-orange-100 text-orange-700 text-xs">Vencida</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="outline" className="text-xs">{tarea.categoria}</Badge>
                        {tarea.fecha_limite && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(tarea.fecha_limite).toLocaleDateString('es-ES')}
                          </span>
                        )}
                        {tarea.expediente_numero && (
                          <span className="text-xs text-blue-600">{tarea.expediente_numero}</span>
                        )}
                      </div>
                    </div>
                    {(tarea.prioridad === 'alta' || tarea.urgente) && (
                      <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Estadísticas de Facturación */}
      {(showFacturacion || showGastos) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {showFacturacion && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5 text-blue-600" />
              Estado de Facturación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Facturado y cobrado</p>
                  <p className="text-2xl font-bold text-green-700 mt-1">{(stats.facturacion_cobrada || 0).toLocaleString('es-ES')}€</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Pendiente de cobro</p>
                  <p className="text-2xl font-bold text-orange-700 mt-1">{(stats.facturacion_pendiente || 0).toLocaleString('es-ES')}€</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Resumen General
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Expedientes totales</span>
                <span className="text-xl font-bold text-gray-900">{stats.expedientes_total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Expedientes activos</span>
                <span className="text-xl font-bold text-green-600">{stats.expedientes_activos}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Contactos registrados</span>
                <span className="text-xl font-bold text-blue-600">{stats.clientes_total}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Resumen de Gastos */}
      {showGastos && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-lg">
              <PiggyBank className="w-5 h-5 text-blue-600" />
              Resumen de Gastos
            </span>
            <Link to="/contabilidad/gastos" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Ver todos →
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Controles</p>
              <p className="text-2xl font-bold text-blue-700 mt-1">{gastosStats.total_controles}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Gastos</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{(gastosStats.total_coste || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}€</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600">Categorías Activas</p>
              <p className="text-2xl font-bold text-purple-700 mt-1">{gastosStats.gastos_por_tipo?.length || 0}</p>
            </div>
          </div>
          
          {/* Mini gráfico de gastos por tipo */}
          {gastosStats.gastos_por_tipo && gastosStats.gastos_por_tipo.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Distribución por Tipo</p>
              <div className="space-y-2">
                {gastosStats.gastos_por_tipo.slice(0, 4).map((item, index) => {
                  const porcentaje = gastosStats.total_coste > 0 
                    ? ((item.total / gastosStats.total_coste) * 100).toFixed(0) 
                    : 0;
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600 truncate max-w-[200px]">{item.tipo}</span>
                        <span className="font-medium">{porcentaje}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${colors[index % colors.length]}`}
                          style={{ width: `${porcentaje}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link to="/contabilidad/gastos/estadisticas" className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium mt-4">
                <BarChart3 className="w-4 h-4 inline mr-1" />
                Ver estadísticas completas →
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default Panel;
